import type { WbsItemDoc, WbsItemStatus, Timestamp } from '@mct/shared';

export interface WbsTreeNode {
  item: WbsItemDoc;
  depth: number;
  children: WbsTreeNode[];
}

export interface DerivedPeriod {
  plannedStart: Date | null;
  plannedEnd: Date | null;
}

/**
 * Segment-aware comparator for sortKey strings like "P0", "1-5", "1-5-3".
 * - Splits on '-'
 * - All-digit segments are compared numerically
 * - Mixed: numeric-first precedence
 * - Fallback: locale-aware string compare
 */
export function compareSortKey(a: string, b: string): number {
  const as = a.split('-');
  const bs = b.split('-');
  const n = Math.max(as.length, bs.length);
  for (let i = 0; i < n; i++) {
    const av = as[i];
    const bv = bs[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    const aIsNum = /^\d+$/.test(av);
    const bIsNum = /^\d+$/.test(bv);
    if (aIsNum && bIsNum) {
      const diff = Number(av) - Number(bv);
      if (diff !== 0) return diff;
      continue;
    }
    if (aIsNum !== bIsNum) return aIsNum ? -1 : 1;
    const c = av.localeCompare(bv, 'ja');
    if (c !== 0) return c;
  }
  return 0;
}

export function sortSiblings<T extends { sortKey: string }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => compareSortKey(a.sortKey, b.sortKey));
}

export function buildTree(items: readonly WbsItemDoc[]): WbsTreeNode[] {
  const byParent = new Map<string | null, WbsItemDoc[]>();
  for (const item of items) {
    const key = item.parentItemId ?? null;
    const arr = byParent.get(key);
    if (arr) arr.push(item);
    else byParent.set(key, [item]);
  }
  const build = (parentId: string | null, depth: number): WbsTreeNode[] => {
    const bucket = byParent.get(parentId) ?? [];
    return sortSiblings(bucket).map((item) => ({
      item,
      depth,
      children: build(item.itemId, depth + 1),
    }));
  };
  return build(null, 0);
}

/**
 * "フェーズ内親タスクのみ折りたたむ" — derive the next expanded set from
 * the current one so that:
 *   - every Phase row stays expanded (its id is kept / added to the set), and
 *   - every parent task sitting directly under a Phase (kind === 'task' with
 *     children) is collapsed (its id is removed from the set).
 *
 * The result is "show me the phase skeleton plus each phase's top-level
 * tasks, but hide the sub-tasks underneath those tasks". Ids that are
 * neither a phase nor a phase-direct parent task are left untouched, so a
 * second toggle elsewhere in the tree (e.g. a root task's expansion) is
 * preserved. Pure — returns a fresh Set and never mutates its inputs.
 */
export function collapsePhaseInnerParents(
  nodes: readonly WbsTreeNode[],
  expanded: ReadonlySet<string>,
): Set<string> {
  const next = new Set(expanded);
  const walk = (ns: readonly WbsTreeNode[]) => {
    for (const node of ns) {
      if (node.item.kind === 'phase') {
        // Keep the phase open so its direct children stay visible.
        next.add(node.item.itemId);
        for (const child of node.children) {
          if (child.item.kind === 'task' && child.children.length > 0) {
            next.delete(child.item.itemId);
          }
        }
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return next;
}

/** Flatten a tree in display order, optionally honoring an expand set. */
export function flattenTree(
  nodes: readonly WbsTreeNode[],
  expanded?: ReadonlySet<string>,
): WbsTreeNode[] {
  const out: WbsTreeNode[] = [];
  const walk = (ns: readonly WbsTreeNode[]) => {
    for (const n of ns) {
      out.push(n);
      if (n.children.length > 0 && (!expanded || expanded.has(n.item.itemId))) {
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return out;
}

function tsToDate(ts: Timestamp | null): Date | null {
  if (!ts) return null;
  return ts.toDate();
}

export function derivePeriod(
  children: readonly Pick<WbsItemDoc, 'plannedStart' | 'plannedEnd'>[],
): DerivedPeriod {
  let start: Date | null = null;
  let end: Date | null = null;
  for (const c of children) {
    const s = tsToDate(c.plannedStart);
    const e = tsToDate(c.plannedEnd);
    if (s && (!start || s.getTime() < start.getTime())) start = s;
    if (e && (!end || e.getTime() > end.getTime())) end = e;
  }
  return { plannedStart: start, plannedEnd: end };
}

export function deriveProgress(
  children: readonly Pick<WbsItemDoc, 'progress' | 'durationDays'>[],
): number {
  let sum = 0;
  let w = 0;
  for (const c of children) {
    const weight = Math.max(c.durationDays ?? 0, 1);
    sum += (c.progress ?? 0) * weight;
    w += weight;
  }
  if (w === 0) return 0;
  return Math.round(sum / w);
}

const TERMINAL_STATUSES: readonly WbsItemStatus[] = ['done', 'cancelled'];

/** True when the row has a plannedEnd in the past and is not yet terminal. */
export function classifyDelay(
  item: Pick<WbsItemDoc, 'plannedEnd' | 'status'>,
  now: Date,
): boolean {
  if (TERMINAL_STATUSES.includes(item.status)) return false;
  const end = tsToDate(item.plannedEnd);
  if (!end) return false;
  return now.getTime() > end.getTime();
}
